import { Module, VuexModule, Mutation, Action } from "vuex-module-decorators";
import { PrintObject } from "@/models/PrintObject";
import { Api } from "@/api/Api";
import { PrintBasket } from "@/models/PrintBasket";
import { Helpers } from "@/global-scripts/helpers";
import { TopicModel } from "@/models/TopicModel";
import { PrintGroup } from "@/models/PrintGroup";
import {Config} from "@/settings/Config";

@Module({
  name: "printBasketModule",
})
export default class PrintBasketModule extends VuexModule {
  public static readonly persisted = ["PrintBasketModule._printBasket"];

  private _printBasket: PrintBasket | undefined = undefined;
  private _printBasketOpen = false;

  private _config = new Config();

  public get printBasketOpen(): boolean {
    return this._printBasketOpen;
  }

  public get printBasket(): PrintBasket {
    return this._printBasket as PrintBasket;
  }

  public get printBasketCount(): number {
    return this.printBasket && this.printBasket.printObjects ? this.printBasket.printObjects.length : 0;
  }

  /**
   * Get list of topics in print basket grouped by parent category
   */
  public get groupedPrintObjects() {
    if (!this.context.rootGetters.allDataLoaded || !this.printBasket || !this.printBasket.printObjects) {
      return [];
    }

    const filteredTopics = this.context.rootGetters.filteredTopics;
    const printObjects = this.printBasket.printObjects;

    printObjects.forEach((printObject) => {
      const topic = this.context.rootGetters.topicByID(printObject.objectID.toString());

      if (topic && topic.title) {
        printObject.title = topic.title;
      }

      printObject.inActiveFilter = filteredTopics.some((t: TopicModel) => this.topicSameAsBasketItem(t, printObject));
    });

    // Group all topics by parent
    const groups = Helpers.groupBy(printObjects, "parentID");
    const result: PrintGroup[] = [];

    groups.forEach((groupItems, key) => {
      const categoryObjectId = Number(key);
      
      // Retrieve the parent category
      const parentCategory = isNaN(categoryObjectId) ? 
          this.context.rootGetters.categoryByID(key) : 
          this.context.rootGetters.categoryByID(categoryObjectId);

      // Push the new PrintGroup with sorted items into the result array
      result.push(new PrintGroup(
          parentCategory,
          Helpers.sortObjectArr<PrintObject>(groupItems, "SortIndexInParent")
      ));
    });

    return result;
  }

  public get topicSameAsBasketItem() {
    return (topic: TopicModel, printObject: PrintObject): boolean => {
      // TODO: Can be removed when enough time has passed so that we can ignore if users have added topics using the old json files.
      //  Check if the ObjectID is a valid integer. If not, it uses the old static topic file structure.
      // If it is a valid integer, it uses the new content nodes.
      const printObjectId = Number(printObject.objectID);
      const printObjectIsFromOldStructure = isNaN(printObjectId);

      if (printObjectIsFromOldStructure) {
        return this._config.useNewContentNodes
            ? printObject.objectID === topic.nodeId
            : printObject.objectID === topic.id;
      }

      // To keep when we remove support for old static files
      return printObjectId === topic.id;
    }
  }

  @Mutation
  setPrintBasketOpen(open: boolean): void {
    this._printBasketOpen = open;
  }

  @Mutation
  setPrintBasket(printBasket: PrintBasket): void {
    this._printBasket = printBasket;
  }

  @Action({ commit: "setPrintBasket", rawError: true })
  async fetchPrintBasket(): Promise<PrintBasket> {
    return await Api.loadPrintBasket();
  }

  @Action({ commit: "setPrintBasket", rawError: true })
  async addToPrintBasket(printObjects: PrintObject[]): Promise<PrintBasket> {
    return await Api.addToPrintBasket(printObjects);
  }

  @Action({ commit: "setPrintBasket", rawError: true })
  async removeFromPrintBasket(printObjects: PrintObject[]): Promise<PrintBasket> {
    return await Api.removeFromPrintBasket(printObjects);
  }

  @Action({ commit: "setPrintBasket", rawError: true })
  async updatePrintBasket(printObjects: PrintObject[]): Promise<PrintBasket> {
    return await Api.uploadPrintBasket(printObjects);
  }
}
